/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// MODIFIED_BY_RYAN to allow clicking FAQ's in blogs so they expand and contract, with animating +/- symbols!
document.addEventListener('DOMContentLoaded', function () {
  console.log("FAQ script running");

  document.querySelectorAll('.faq-question').forEach(function(h4, index) {
    console.log(`Processing FAQ #${index + 1}: ${h4.textContent}`);

    h4.style.display = "flex";
    h4.style.justifyContent = "flex-start";
    h4.style.alignItems = "center";
    h4.style.gap = "8px";
    h4.style.cursor = "pointer";

    let text = h4.innerHTML.trim();
    let numberMatch = text.match(/^(\d+[\.\)]*)\s*(.*)$/);
    if (numberMatch) {
      h4.innerHTML = `<strong>${numberMatch[1]}</strong> ${numberMatch[2]}`;
    }

    let symbolContainer = document.createElement('div');
    symbolContainer.style.minWidth = "40px";
    symbolContainer.style.marginLeft = "auto";
    symbolContainer.style.display = "flex";
    symbolContainer.style.justifyContent = "center";
    symbolContainer.style.alignItems = "center";

    let symbol = document.createElement('span');
    symbol.innerHTML = "+";
    symbol.classList.add('faq-symbol');
    symbol.style.fontSize = "24px";
    symbol.style.display = "inline-flex";
    symbol.style.justifyContent = "center";
    symbol.style.alignItems = "center";
    symbol.style.transition = "transform 0.4s ease-out";
    symbol.style.transformOrigin = "center";

    symbolContainer.appendChild(symbol);
    h4.appendChild(symbolContainer);

    let answerWrapper = document.createElement('div');
    answerWrapper.classList.add('faq-answer');

    let nextElem = h4.nextElementSibling;
    let elementsToMove = [];
    while (nextElem && !nextElem.classList.contains('faq-question') && nextElem.tagName !== 'H2') {
      if (!nextElem.textContent.trim()) {
        console.log(`Skipping empty: ${nextElem.outerHTML}`);
        nextElem.remove();
      } else {
        console.log(`Capturing: ${nextElem.outerHTML}`);
        elementsToMove.push(nextElem);
      }
      nextElem = nextElem.nextElementSibling;
    }

    elementsToMove.forEach(elem => answerWrapper.appendChild(elem));
    h4.parentNode.insertBefore(answerWrapper, h4.nextElementSibling);

    // Set initial styles
    answerWrapper.style.maxHeight = "0px";
    answerWrapper.style.opacity = "0";
    answerWrapper.style.overflow = "hidden";
    answerWrapper.style.transition = "max-height 0.5s ease-in-out, opacity 0.3s ease-in-out";

    h4.setAttribute('role', 'button');
    h4.setAttribute('aria-expanded', 'false');
    answerWrapper.setAttribute('aria-hidden', 'true');

    h4.addEventListener('click', function() {
      let isExpanding = h4.getAttribute('aria-expanded') === 'false';
      if (isExpanding) {
        h4.setAttribute('aria-expanded', 'true');
        answerWrapper.setAttribute('aria-hidden', 'false');
        answerWrapper.style.maxHeight = `${answerWrapper.scrollHeight}px`;
        answerWrapper.style.opacity = "1";
        symbol.innerHTML = "−";
        symbol.style.transform = "rotate(360deg)";
      } else {
        h4.setAttribute('aria-expanded', 'false');
        answerWrapper.setAttribute('aria-hidden', 'true');
        answerWrapper.style.maxHeight = "0px";
        answerWrapper.style.opacity = "0";
        symbol.innerHTML = "+";
        symbol.style.transform = "rotate(0deg)";
      }
    });
  });
});

/* CUSTOM_SWATCHES_BY_CLAUDE — Scrollable swatch strip with lazy-loaded overflow */
(function() {
  'use strict';

  function escapeHtml(str) {
    var el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  /* Hydrate deferred swatches from JSON data attribute */
  function hydrateSwatches(container) {
    var json = container.getAttribute('data-deferred-swatches');
    if (!json) return;

    var swatches;
    try { swatches = JSON.parse(json); } catch (e) { return; }

    var optionId = container.dataset.optionId;
    var shape = container.dataset.swatchShape || 'circle';
    var style = container.dataset.colorsStyle || 'native';
    var isNative = container.dataset.isNative === 'true';
    var swatchSize = container.dataset.swatchSize || '48';

    var frag = document.createDocumentFragment();

    swatches.forEach(function(s) {
      /* <input> */
      var input = document.createElement('input');
      input.type = 'radio';
      input.className = 'opt-btn visually-hidden js-option';
      input.name = optionId + '-option';
      input.id = optionId + '-opt-' + s.index;
      input.value = s.value;
      input.dataset.variantId = s.variantId;
      if (s.mediaId) input.dataset.mediaId = s.mediaId;
      if (s.isSelected) input.checked = true;

      /* <label> */
      var label = document.createElement('label');
      label.setAttribute('for', optionId + '-opt-' + s.index);

      var cls = 'opt-label opt-label--swatch relative swatch-shape--' + shape;
      if (shape !== 'circle') cls += ' swatch-shape--not-circle';

      if (isNative) {
        if (s.type === 'native-image') {
          cls += ' btn swatch--native';
          label.setAttribute('data-swatch', '');
          var imgHtml;
          if (shape === 'natural') {
            imgHtml = '<div class="opt-label__native-media media">' +
              '<img loading="lazy" src="' + s.swatchImageUrl + '" width="64">' +
              '</div>';
          } else {
            imgHtml = '<div class="opt-label__native-media media absolute inset-0">' +
              '<img loading="lazy" class="img-fit" src="' + s.swatchImageUrl + '" width="64">' +
              '</div>';
          }
          label.innerHTML = imgHtml + '<span class="visually-hidden js-value">' + escapeHtml(s.value) + '</span>';
        } else if (s.type === 'native-color') {
          cls += ' btn';
          label.setAttribute('data-swatch', s.value.replace(/"/g, '').toLowerCase());
          label.style.setProperty('--swatch-color', 'rgb(' + s.swatchColorRgb + ')');
          label.style.setProperty('--swatch-image', 'none');
          label.innerHTML = '<span class="visually-hidden js-value">' + escapeHtml(s.value) + '</span>';
        }
      } else {
        /* Legacy swatch or variant-images */
        if (style === 'variant-images' && s.variantImageUrl) {
          cls += ' swatch--variant-image';
          label.setAttribute('data-swatch', '');
          var viHtml;
          if (shape === 'natural') {
            viHtml = '<img loading="lazy" class="bg-theme-bg" src="' + s.variantImageUrl + '" ' +
              'height="' + Math.round(parseInt(swatchSize) / (s.variantImageAspect || 1)) + '" ' +
              'width="' + swatchSize + '" alt="' + escapeHtml(s.variantImageAlt || '') + '">';
          } else {
            viHtml = '<img loading="lazy" class="img-fit bg-theme-bg" src="' + s.variantImageUrl + '" ' +
              'height="' + swatchSize + '" width="' + swatchSize + '" alt="' + escapeHtml(s.variantImageAlt || '') + '">';
          }
          label.innerHTML = viHtml + '<span class="visually-hidden">' + escapeHtml(s.value) + '</span>';
        } else {
          label.setAttribute('data-swatch', s.value.replace(/"/g, '').toLowerCase());
          label.setAttribute('title', s.value.replace(/"/g, '`'));
          label.innerHTML = '<span class="visually-hidden">' + escapeHtml(s.value) + '</span>';
        }
      }

      label.className = cls;
      frag.appendChild(input);
      frag.appendChild(label);
    });

    container.appendChild(frag);
    container.removeAttribute('data-deferred-swatches');
    updateScrollState(container);
  }

  /* Update fade-gradient classes based on scroll position (RAF-throttled) */
  var scrollRAFs = new WeakMap();
  function updateScrollState(container) {
    if (scrollRAFs.has(container)) return;
    scrollRAFs.set(container, requestAnimationFrame(function() {
      scrollRAFs.delete(container);
      var scrollLeft = container.scrollLeft;
      var maxScroll = container.scrollWidth - container.clientWidth;

      container.classList.toggle('is-scrolled-start', scrollLeft > 4);
      container.classList.toggle('is-scrolled-end', maxScroll - scrollLeft < 4);

      if (maxScroll <= 4) {
        container.classList.add('is-scrolled-end');
        container.classList.remove('has-overflow');
      } else {
        container.classList.add('has-overflow');
      }
    }));
  }

  /* Desktop drag-to-scroll — listeners on document so dragging continues
     even when the mouse drifts vertically off the narrow swatch row */
  function initDragScroll(container) {
    var isDown = false, startX, scrollLeft, hasDragged;

    container.addEventListener('mousedown', function(e) {
      isDown = true;
      hasDragged = false;
      startX = e.pageX;
      scrollLeft = container.scrollLeft;
      /* Stop parent carousels (e.g. Recently Viewed) from also dragging */
      e.stopPropagation();
    });

    var dragOrigin = 0;
    function onMouseMove(e) {
      if (!isDown) return;
      var walk = e.pageX - startX;
      if (Math.abs(walk) > 5) {
        if (!hasDragged) {
          hasDragged = true;
          /* Reset origin to current position so the first frame has zero delta */
          dragOrigin = e.pageX;
          document.body.style.userSelect = 'none';
          document.body.style.webkitUserSelect = 'none';
          container.classList.add('is-dragging');
          return;
        }
        e.preventDefault();
        var delta = e.pageX - dragOrigin;
        container.scrollLeft = scrollLeft - (delta * 2);
      }
    }

    function onMouseUp() {
      if (!isDown) return;
      isDown = false;
      container.classList.remove('is-dragging');
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    /* Prevent swatch click from firing if user was dragging */
    container.addEventListener('click', function(e) {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  /* Persistent IntersectionObserver for lazy hydration */
  var hydrationObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            hydrateSwatches(entry.target);
            hydrationObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px 0px' })
    : null;

  /* Set up a single swatch container (scroll, drag, hydration) */
  function initContainer(container) {
    if (container.dataset.swatchStripInit) return;
    container.dataset.swatchStripInit = 'true';

    container.addEventListener('scroll', function() {
      updateScrollState(container);
    }, { passive: true });

    initDragScroll(container);
    updateScrollState(container);

    /* Intercept label clicks to prevent browser's native focus-scroll.
       Manually check the radio and dispatch change with preventScroll. */
    container.addEventListener('click', function(e) {
      var label = e.target.closest('.opt-label');
      if (!label) return;
      var inputId = label.getAttribute('for');
      if (!inputId) return;
      var input = document.getElementById(inputId);
      if (!input) return;

      e.preventDefault();
      e.stopPropagation();

      input.checked = true;
      input.focus({ preventScroll: true });
      input.dispatchEvent(new Event('change', { bubbles: true }));

      /* Mobile tap pulse — grow then shrink back */
      if (window.innerWidth <= 768) {
        label.classList.add('swatch-pulse');
        setTimeout(function() { label.classList.remove('swatch-pulse'); }, 200);
      }
    }, true);

    /* Add scroll-more chevron arrow if there's overflow */
    var maxScroll = container.scrollWidth - container.clientWidth;
    if (maxScroll > 4 || container.hasAttribute('data-deferred-swatches')) {
      /* Wrap each swatch row in its own relative container for arrow positioning */
      var wrap = document.createElement('div');
      wrap.className = 'swatch-scroll-wrap';
      container.parentNode.insertBefore(wrap, container);
      wrap.appendChild(container);

      var arrow = document.createElement('button');
      arrow.className = 'swatch-scroll-arrow';
      arrow.setAttribute('aria-label', 'Scroll for more colors');
      arrow.innerHTML = '<svg viewBox="0 0 8 12"><polyline points="1,1 6,6 1,11"/></svg>';
      wrap.appendChild(arrow);

      arrow.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        container.scrollBy({ left: container.clientWidth * 0.7, behavior: 'smooth' });
      });
    }

    /* Queue deferred swatches for lazy hydration */
    if (container.hasAttribute('data-deferred-swatches')) {
      if (hydrationObserver) {
        hydrationObserver.observe(container);
      } else {
        hydrateSwatches(container);
      }
    }
  }

  /* Initialize all swatch containers currently on the page */
  function initSwatchStrips() {
    document.querySelectorAll('.card__swatches').forEach(initContainer);
  }

  /* Run on load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwatchStrips);
  } else {
    initSwatchStrips();
  }

  /* Re-init after Shopify section rendering (theme editor) */
  document.addEventListener('shopify:section:load', initSwatchStrips);

  /* MutationObserver: catch cards added by infinite scroll / AJAX pagination */
  var domObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        /* If the added node IS a swatch container */
        if (node.matches && node.matches('.card__swatches')) {
          initContainer(node);
        }
        /* Or if it contains swatch containers (e.g. a product card wrapper) */
        if (node.querySelectorAll) {
          node.querySelectorAll('.card__swatches').forEach(initContainer);
        }
      });
    });
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
})();
/* END CUSTOM_SWATCHES_BY_CLAUDE */

/* CUSTOM_SWATCHES_BY_CLAUDE — Global ATC button SVG checkmark injection.
   Watches for is-success class on ATC buttons and injects a matching SVG checkmark. */
(function() {
  var checkmarkSvg =
    '<span class="js-btn-checkmark">' +
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polyline points="20 6 9 17 4 12"/>' +
      '</svg>' +
    '</span>';

  var observer = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var target = mutations[i].target;
      if (target.matches && target.matches('.btn[name="add"]') && target.classList.contains('is-success') && !target.querySelector('.js-btn-checkmark')) {
        target.insertAdjacentHTML('beforeend', checkmarkSvg);
      }
    }
  });

  observer.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
})();
/* END CUSTOM_SWATCHES_BY_CLAUDE — Global ATC checkmark */
